import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useRecoilState } from "recoil";
import api from "../../api";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ViewTradeAccordion from "../../components/Trades/ViewTradeAccordion";
import PopupModal from "../../components/Common/PopupModal";
import { tradesState } from "../../Recoil/atom";

const ManageTrades = () => {
  const [trades, setViewTrades] = useRecoilState(tradesState);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const user = localStorage.getItem("authUser")?.toLowerCase() || "";
  const userLower = user;
  // Popup state variables
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("");
  // Modal and current trade state
  const [viewTradeModal, setViewTradeModal] = useState(false);
  const [currentTrade, setCurrentTrade] = useState(null);

  useEffect(() => {
    // Initial load without referencing fetchTrades to avoid TDZ lint
    (async () => {
      try {
        const res = await api.get("/v1/trades", { withCredentials: true });
        const list = res.data?.trades || [];
        setViewTrades(list);
      } catch (err) {
        console.error("Failed to fetch trades", err);
      }
    })();
  }, [setViewTrades]);

  const fetchTrades = useCallback(async () => {
    try {
      const res = await api.get("/v1/trades", { withCredentials: true });
      const list = res.data?.trades || [];
      // Keep global Recoil state in sync for sidebar badges
      setViewTrades(list);
    } catch (err) {
      console.error("Failed to fetch trades", err);
    }
  }, [setViewTrades]);

  const handleSelectTrade = (tradeId) => {
    setSelectedTrades((prevSelected) =>
      prevSelected.includes(tradeId)
        ? prevSelected.filter((id) => id !== tradeId)
        : [...prevSelected, tradeId],
    );
  };

  // Open the modal for a given trade.
  // We determine the trade type ("received" or "sent") by comparing the trade sender/recipient to the current user.
  const handleViewTrade = async (trade) => {
    try {
      // Only mark as viewed if current user is the recipient.
      if (
        trade.recipient_username &&
        trade.recipient_username.toLowerCase() === userLower
      ) {
        await api.put(
          `/v1/trade/viewed/${trade.tradeid}`,
          {},
          { withCredentials: true },
        );
        // Optionally refresh trades after marking as viewed.
        fetchTrades();
        setViewTrades((prevTrades) =>
          prevTrades.map((t) =>
            t.tradeid === trade.tradeid ? { ...t, seen: 1 } : t,
          ),
        );
      }
      // In any case, display the trade details in the modal.
      setCurrentTrade(trade);
      setViewTradeModal(true);
    } catch (err) {
      console.error(`Failed to mark trade ${trade.tradeid} as viewed`, err);
    }
  };

  const handleDeleteTrade = async (trade) => {
    try {
      await api.delete(`/v1/trade/${trade.tradeid}`, { withCredentials: true });
      // Update local and global state immediately for responsive badges
      setViewTrades((prev) => prev.filter((t) => t.tradeid !== trade.tradeid));
      // Then refresh from server to stay in sync
      fetchTrades();
    } catch (err) {
      console.error(`Failed to delete trade ${trade.tradeid}`, err);
    }
  };

  const handleDeleteSelectedTrades = async () => {
    for (let tradeId of selectedTrades) {
      try {
        await api.delete(`/v1/trade/${tradeId}`, { withCredentials: true });
      } catch (err) {
        console.error(`Failed to delete trade ${tradeId}`, err);
      }
    }
    setSelectedTrades([]);
    // Optimistically update state for immediate UI feedback
    setViewTrades((prev) => prev.filter((t) => !selectedTrades.includes(t.tradeid)));
    // Then refresh from server
    fetchTrades();
  };

  const formatEpoch = (epoch) => {
    const date = new Date(epoch * 1000);
    return date.toLocaleString();
  };

  // Split trades into received and sent
  const tradesReceived = trades.filter(
    (trade) =>
      trade.recipient_username &&
      trade.recipient_username.toLowerCase() === userLower,
  );
  const tradesSent = trades.filter(
    (trade) =>
      trade.sender_username &&
      trade.sender_username.toLowerCase() === userLower,
  );

  // Function to display the popup message
  const showPopupMessage = (message, detail, color) => {
    setPopupMessage(message);
    setPopupColor(color);
    setShowPopup(true);
    // Hide the popup after 3 seconds
    setTimeout(() => {
      setShowPopup(false);
    }, 3000);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Trades" breadcrumbItem="Manage Trades" />

          {/* Popup Component */}
          <PopupModal
            isOpen={showPopup}
            message={popupMessage}
            backgroundColor={popupColor}
            onClose={() => setShowPopup(false)}
          />

          <Row className="mb-3">
            <Col>
              <Button
                color="danger"
                onClick={handleDeleteSelectedTrades}
                disabled={selectedTrades.length === 0}
              >
                Delete Selected
              </Button>
            </Col>
          </Row>

          {currentTrade && (
            <Modal
              isOpen={viewTradeModal}
              toggle={() => setViewTradeModal(false)}
              size="lg"
              centered
            >
              <ModalHeader toggle={() => setViewTradeModal(false)}>
                Trade Details
              </ModalHeader>
              <ModalBody>
                <ViewTradeAccordion
                  trade={currentTrade}
                  tradeType={
                    currentTrade.sender_username &&
                    currentTrade.sender_username.toLowerCase() === userLower
                      ? "sent"
                      : "received"
                  }
                  showPopupMessage={showPopupMessage}
                  onTradeCompleted={(completedTradeId) => {
                    setViewTrades((prevTrades) =>
                      prevTrades.filter(
                        (trade) => trade.tradeid !== completedTradeId,
                      ),
                    );
                    setViewTradeModal(false); // This will close the modal.
                  }}
                />
              </ModalBody>
              <ModalFooter>
                {/* Optionally include a Close button */}
                <Button
                  color="secondary"
                  onClick={() => setViewTradeModal(false)}
                >
                  Close
                </Button>
              </ModalFooter>
            </Modal>
          )}

          {/* Trades Received Section */}
          <Card>
            <CardBody>
              <h4>Trades Received</h4>
              <Table responsive striped className="align-middle table-nowrap">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          selectedTrades.length === tradesReceived.length &&
                          tradesReceived.length > 0
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTrades(
                              tradesReceived.map((trade) => trade.tradeid),
                            );
                          } else {
                            setSelectedTrades([]);
                          }
                        }}
                      />
                    </th>
                    <th>From</th>
                    <th>Trade Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tradesReceived.length > 0 ? (
                    tradesReceived.map((trade) => (
                      <tr key={trade.tradeid}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTrades.includes(trade.tradeid)}
                            onChange={() => handleSelectTrade(trade.tradeid)}
                          />
                        </td>
                        <td>
                          <Link
                            to={`/playerprofile?user=${trade.sender_username}`}
                            style={{ textDecoration: "none", color: "#007bff" }}
                          >
                            {trade.sender_username}
                          </Link>
                        </td>
                        <td>{formatEpoch(trade.tradetime)}</td>
                        <td>
                          <span
                            className={`badge ${trade.seen ? "bg-success" : "bg-warning"}`}
                          >
                            {trade.seen ? "Viewed" : "New Trade"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => handleViewTrade(trade)}
                            >
                              View
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDeleteTrade(trade)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No trades received.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Trades Sent Section */}
          <Card className="mt-4">
            <CardBody>
              <h4>Trades Sent</h4>
              <Table responsive striped className="align-middle table-nowrap">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          selectedTrades.length === tradesSent.length &&
                          tradesSent.length > 0
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTrades(
                              tradesSent.map((trade) => trade.tradeid),
                            );
                          } else {
                            setSelectedTrades([]);
                          }
                        }}
                      />
                    </th>
                    <th>To</th>
                    <th>Trade Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tradesSent.length > 0 ? (
                    tradesSent.map((trade) => (
                      <tr key={trade.tradeid}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTrades.includes(trade.tradeid)}
                            onChange={() => handleSelectTrade(trade.tradeid)}
                          />
                        </td>
                        <td>
                          <Link
                            to={`/playerprofile?user=${trade.recipient_username}`}
                            style={{ textDecoration: "none", color: "#007bff" }}
                          >
                            {trade.recipient_username}
                          </Link>
                        </td>
                        <td>{formatEpoch(trade.tradetime)}</td>
                        <td>
                          <span
                            className={`badge ${trade.seen ? "bg-green" : "bg-warning"}`}
                          >
                            {trade.seen ? "Viewed" : "Unviewed"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => handleViewTrade(trade)}
                            >
                              View
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDeleteTrade(trade)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No trades sent.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManageTrades;
